import { Injectable, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

const PREF_KEY       = 'pax_favorites';
const LINES_PREF_KEY = 'pax_fav_lines';

export interface FavoriteRoute {
  id: string; // `line-${lineId}-route-${routeId}`
  lineName: string;
  lineColor: string;
  routeName: string;
  lineId: number;
  routeId: number;
  savedAt: string;
}

export interface FavoriteLine {
  lineId:    number;
  lineName:  string;
  lineColor: string;
  savedAt:   string;
}

@Injectable({ providedIn: 'root' })
export class FavoritesService {
  readonly favorites     = signal<FavoriteRoute[]>([]);
  readonly favoriteLines = signal<FavoriteLine[]>([]);

  async load(): Promise<void> {
    const [routeRes, lineRes] = await Promise.all([
      Preferences.get({ key: PREF_KEY }),
      Preferences.get({ key: LINES_PREF_KEY }),
    ]);
    if (routeRes.value) this.favorites.set(JSON.parse(routeRes.value));
    if (lineRes.value)  this.favoriteLines.set(JSON.parse(lineRes.value));
  }

  // ── Line-level favorites ───────────────────────────────────────────────────

  async toggleLine(line: { id: number; name: string; colorHex: string }): Promise<void> {
    const already = this.favoriteLines().find(f => f.lineId === line.id);
    let updated: FavoriteLine[];
    if (already) {
      updated = this.favoriteLines().filter(f => f.lineId !== line.id);
    } else {
      updated = [
        ...this.favoriteLines(),
        {
          lineId:    line.id,
          lineName:  line.name,
          lineColor: line.colorHex || '#32737E',
          savedAt:   new Date().toISOString(),
        },
      ];
    }
    this.favoriteLines.set(updated);
    await Preferences.set({ key: LINES_PREF_KEY, value: JSON.stringify(updated) });
  }

  isLineFavorite(lineId: number): boolean {
    return this.favoriteLines().some(f => f.lineId === lineId);
  }

  async removeLine(lineId: number): Promise<void> {
    const updated = this.favoriteLines().filter(f => f.lineId !== lineId);
    this.favoriteLines.set(updated);
    await Preferences.set({ key: LINES_PREF_KEY, value: JSON.stringify(updated) });
  }

  /** Puts a removed line back exactly as it was, for undo. */
  async restoreLine(fav: FavoriteLine): Promise<void> {
    if (this.favoriteLines().some(f => f.lineId === fav.lineId)) return;
    const updated = [...this.favoriteLines(), fav];
    this.favoriteLines.set(updated);
    await Preferences.set({ key: LINES_PREF_KEY, value: JSON.stringify(updated) });
  }

  /** Puts a removed route back exactly as it was, for undo. */
  async restoreRoute(fav: FavoriteRoute): Promise<void> {
    if (this.favorites().some(f => f.id === fav.id)) return;
    const updated = [...this.favorites(), fav];
    this.favorites.set(updated);
    await Preferences.set({ key: PREF_KEY, value: JSON.stringify(updated) });
  }

  async save(fav: Omit<FavoriteRoute, 'id' | 'savedAt'>): Promise<void> {
    const id = `line-${fav.lineId}-route-${fav.routeId}`;
    const already = this.favorites().some(f => f.id === id);
    if (already) return;

    const updated = [
      ...this.favorites(),
      { ...fav, id, savedAt: new Date().toISOString() },
    ];
    this.favorites.set(updated);
    await Preferences.set({ key: PREF_KEY, value: JSON.stringify(updated) });
  }

  async remove(id: string): Promise<void> {
    const updated = this.favorites().filter(f => f.id !== id);
    this.favorites.set(updated);
    await Preferences.set({ key: PREF_KEY, value: JSON.stringify(updated) });
  }

  isFavorite(lineId: number, routeId: number): boolean {
    return this.favorites().some(
      f => f.lineId === lineId && f.routeId === routeId,
    );
  }
}
